<template>
  <div>
    <div class="form-group p-t-10">
      <div class="col-md-4 control-label">Select your business entity type *</div>
      <div class="col-md-6 col-xs-8">
        <div class="radio">
          <label>
            <input
              v-model="hasCaBusinessEntity"
              :value="1"
              type="radio"
              name="has_ca_business_entity"
            />
            <i class="input-helper" />
            I have a business entity setup
          </label>
        </div>
        <div class="radio">
          <label>
            <input
              v-model="hasCaBusinessEntity"
              :value="0"
              type="radio"
              name="has_ca_business_entity"
            />
            <i class="input-helper" />
            I do not have a business entity setup
          </label>
        </div>
      </div>
    </div>

    <div v-show="hasCaBusinessEntity">
      <hr />
      <div class="form-group p-t-30">
        <label class="col-md-4 control-label" for="caBusinessEntityNameInput">
          Business entity name *
        </label>
        <div class="col-md-6 col-xs-8">
          <input
            id="caBusinessEntityNameInput"
            v-model="caBusinessEntityName"
            name="ca_business_entity_name"
            placeholder="Business entity name"
            type="text"
            required="required"
            class="form-control"
          />
        </div>
      </div>

      <div v-if="currentSelectedLocation?.state !== 'QC'" class="form-group">
        <label for="ca_business_number" class="col-md-4 control-label">
          Business number
          <CompanyNumberTooltip market="CA" />
        </label>
        <div class="col-md-6">
          <input
            id="ca_business_number"
            v-model="caBusinessNumber"
            class="form-control"
            placeholder="Business number e.g. 123456789RT1234"
            name="ca_business_number"
            type="text"
          />
        </div>
      </div>

      <div v-else class="form-group">
        <label for="quebec_enterprise_number" class="col-md-4 control-label">
          Québec enterprise number (NEQ)
          <CompanyNumberTooltip market="CA_Q" />
        </label>
        <div class="col-md-6">
          <input
            id="quebec_enterprise_number"
            v-model="quebecEnterpriseNumber"
            class="form-control"
            placeholder="Québec enterprise number e.g. 1234567890"
            name="quebec_enterprise_number"
            type="text"
          />
        </div>
      </div>

      <div class="form-group p-t-10">
        <div class="col-md-4 control-label">Are you registered for GST/HST?</div>
        <div class="col-md-6 col-xs-8">
          <div class="radio">
            <label>
              <input
                v-model="isGstHstRegistered"
                :value="1"
                type="radio"
                name="is_gst_hst_registered"
              />
              <i class="input-helper" />
              Yes, I have a GST/HST registration
            </label>
          </div>
          <div class="radio">
            <label>
              <input
                v-model="isGstHstRegistered"
                :value="0"
                type="radio"
                name="is_gst_hst_registered"
              />
              <i class="input-helper" />
              No, I will be billing without GST/HST
            </label>
          </div>
        </div>
      </div>

      <div v-show="isGstHstRegistered === 1" class="form-group">
        <label for="gst_hst_number" class="col-md-4 control-label">GST/HST number *</label>
        <div class="col-md-6">
          <input
            id="gst_hst_number"
            v-model="gstHstNumber"
            class="form-control"
            placeholder="GST/HST number"
            name="vat_no"
            type="text"
            required
          />
        </div>
      </div>
    </div>

    <div v-show="!hasCaBusinessEntity">
      <hr />
      <div class="form-group">
        <label for="ca_social_insurance_number" class="col-md-4 control-label">
          Social insurance number (SIN)
          <WdsTooltip
            text="If you receive more than $500 in a financial year, your SIN will be used for us to file an annual T4A slip with the Canadian Revenue Agency."
            left
          >
            <slot>
              <i class="c-indigo-500 zmdi zmdi-help"></i>
            </slot>
          </WdsTooltip>
        </label>
        <div class="col-md-6">
          <input
            id="ca_social_insurance_number"
            v-model="caSocialInsuranceNumber"
            class="form-control"
            placeholder="Social insurance number e.g. 123456789"
            name="ca_social_insurance_number"
            type="text"
          />
        </div>
      </div>
    </div>

    <div class="form-group">
      <div class="col-sm-6 col-sm-offset-4 col-xs-12">
        <SubmitButton :disabled="!canSave" :text="$t('Save')" auto-submit />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable import/extensions */
import { WdsTooltip } from '@worksome/wds-components'
import CompanyNumberTooltip from '@/components/compliances/BusinessEntity/CompanyNumberTooltip.vue'
import { booleanToIntNullable, intToBooleanNullable } from '@/utils/forms/ValueNormalisers'
import { deepClone } from '@/utils/tools'
import SubmitButton from '@/components/SubmitButton.vue'

export default {
  name: 'FreelancerCaBusinessEntityForm',
  components: {
    SubmitButton,
    CompanyNumberTooltip,
    WdsTooltip,
  },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    currentSelectedLocation: {
      type: Object,
      required: false,
    },
  },
  data() {
    const internalBusinessEntity = deepClone(this.businessEntity)
    const internalComplianceData = deepClone(this.complianceData)
    return {
      internalBusinessEntity,
      internalComplianceData,
    }
  },
  computed: {
    caBusinessEntityName: {
      get() {
        return this.internalBusinessEntity.companyName
      },
      set(caBusinessEntityName) {
        this.internalBusinessEntity.companyName = caBusinessEntityName
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    caBusinessNumber: {
      get() {
        return this.internalBusinessEntity.companyNo
      },
      set(caBusinessNumber) {
        this.internalBusinessEntity.companyNo = caBusinessNumber
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    quebecEnterpriseNumber: {
      get() {
        return this.internalBusinessEntity.companyNo
      },
      set(caBusinessNumber) {
        this.internalBusinessEntity.companyNo = caBusinessNumber
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    caSocialInsuranceNumber: {
      get() {
        return this.hasCaBusinessEntity === 0 ? this.internalBusinessEntity.taxNo : null
      },
      set(caSocialInsuranceNumber) {
        if (this.hasCaBusinessEntity === 0) {
          this.internalBusinessEntity.taxNo = caSocialInsuranceNumber
          this.$emit('update:businessEntity', this.internalBusinessEntity)
        }
      },
    },
    canSave() {
      if (this.hasCaBusinessEntity === null) {
        return false
      }
      if (this.hasCaBusinessEntity === 0) {
        return true
      }
      // For business entity, require business name
      if (!this.caBusinessEntityName || this.caBusinessEntityName === '') {
        return false
      }
      // If GST/HST registered, require GST number
      if (this.isGstHstRegistered === 1 && (!this.gstHstNumber || this.gstHstNumber === '')) {
        return false
      }
      return true
    },
    hasCaBusinessEntity: {
      get() {
        return booleanToIntNullable(
          this.internalComplianceData.complianceInput?.data?.has_ca_business_entity ?? null,
        )
      },
      set(hasCaBusinessEntity) {
        if (this.internalComplianceData.complianceInput === null) {
          this.internalComplianceData.complianceInput = { data: {} }
        }
        this.internalComplianceData.complianceInput.data.has_ca_business_entity =
          intToBooleanNullable(hasCaBusinessEntity)

        if (hasCaBusinessEntity === 0) {
          this.caBusinessEntityName = null
          this.caBusinessNumber = null
          this.quebecEnterpriseNumber = null
          this.internalBusinessEntity.taxNo = null
          this.internalBusinessEntity.data.gst_hst_registered = null
        } else if (hasCaBusinessEntity === 1) {
          this.internalBusinessEntity.taxNo = null
        }

        this.$emit('update:complianceData', this.internalComplianceData)
      },
    },
    isGstHstRegistered: {
      get() {
        return booleanToIntNullable(this.internalBusinessEntity.data.gst_hst_registered ?? null)
      },
      set(isGstHstRegistered) {
        this.internalBusinessEntity.data.gst_hst_registered =
          intToBooleanNullable(isGstHstRegistered)
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    gstHstNumber: {
      get() {
        return this.hasCaBusinessEntity === 1 ? this.internalBusinessEntity.taxNo : null
      },
      set(gstHstNumber) {
        if (this.hasCaBusinessEntity === 1) {
          this.internalBusinessEntity.taxNo = gstHstNumber
          this.$emit('update:businessEntity', this.internalBusinessEntity)
        }
      },
    },
  },
  watch: {
    currentSelectedLocation(val, oldVal) {
      if (oldVal?.state !== val?.state) {
        this.internalBusinessEntity.companyNo = ''
      }
    },
  },
}
</script>
